import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import {
  listSops,
  listSopAssignees,
  softDeleteSop,
  getSopSignedUrl,
  getSopCoverSignedUrls,
  type Sop,
} from "@/lib/sops";
import { SopCard } from "@/components/sops/SopCard";
import { UploadSopDialog } from "@/components/sops/UploadSopDialog";
import type { PickerProfile } from "@/components/sops/AssigneePicker";

export const Route = createFileRoute("/_app/sops")({
  component: SopsPage,
  head: () => ({ meta: [{ title: "SOPs — Standard Operating Procedures" }] }),
});

const DEPARTMENTS: Department[] = [
  "editor",
  "graphic_designer",
  "ai",
  "marketer",
  "seo",
  "smm",
  "scriptwriter",
  "calling",
  "hr",
  "admin",
];

function SopsPage() {
  const qc = useQueryClient();
  const { user, role } = useAuth();
  const canManage = role === "admin";

  const { data: sops = [] } = useQuery({ queryKey: ["sops"], queryFn: listSops });

  const coverPaths = useMemo(
    () =>
      sops
        .map((s) => s.cover_image_path)
        .filter((p): p is string => !!p),
    [sops],
  );

  const { data: coverUrls = {} } = useQuery({
    queryKey: ["sops-covers", coverPaths.join("|")],
    enabled: coverPaths.length > 0,
    queryFn: () => getSopCoverSignedUrls(coverPaths),
    staleTime: 4 * 60 * 1000,
  });

  const { data: profiles = [] } = useQuery<PickerProfile[]>({
    queryKey: ["sops-profiles"],
    enabled: canManage,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, department, avatar_url")
        .eq("status", "active")
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as PickerProfile[];
    },
  });

  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState<"all" | Department>("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<Sop | null>(null);
  const [editingAssignees, setEditingAssignees] = useState<string[]>([]);
  const [confirmDel, setConfirmDel] = useState<Sop | null>(null);

  // In-app preview
  const [previewSop, setPreviewSop] = useState<Sop | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const visible = useMemo(() => {
    let list = sops;
    if (deptFilter !== "all") list = list.filter((s) => s.department === deptFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          (s.description ?? "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [sops, deptFilter, search]);

  const openEdit = async (sop: Sop) => {
    setEditing(sop);
    try {
      const rows = await listSopAssignees(sop.id);
      setEditingAssignees(rows.map((r) => r.user_id));
    } catch {
      setEditingAssignees([]);
    }
  };

  const preview = async (sop: Sop) => {
    setPreviewSop(sop);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const url = await getSopSignedUrl(sop.file_path);
      setPreviewUrl(url);
    } catch (e) {
      toast.error((e as Error).message || "Could not open file");
      setPreviewSop(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewSop(null);
    setPreviewUrl(null);
    setPreviewLoading(false);
  };

  const download = async (sop: Sop) => {
    try {
      const url = await getSopSignedUrl(sop.file_path);
      const a = document.createElement("a");
      a.href = url;
      a.download = sop.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error((e as Error).message || "Download failed");
    }
  };

  const delMut = useMutation({
    mutationFn: async (sop: Sop) => softDeleteSop(sop.id),
    onSuccess: () => {
      toast.success("SOP deleted");
      qc.invalidateQueries({ queryKey: ["sops"] });
      setConfirmDel(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mx-auto w-full max-w-7xl p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Standard Operating Procedures</h1>
          <p className="text-[12px] text-muted-foreground">
            {canManage
              ? "Manage SOPs across all departments."
              : "Reference SOPs for your work."}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setUploadOpen(true)}>
            <Plus className="h-4 w-4" /> Upload SOP
          </Button>
        )}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SOPs…"
            className="pl-8"
          />
        </div>
        {canManage && (
          <Select
            value={deptFilter}
            onValueChange={(v) => setDeptFilter(v as "all" | Department)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {DEPARTMENTS.map((d) => (
                <SelectItem key={d} value={d}>
                  {DEPARTMENT_LABELS[d]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-[13px] text-muted-foreground">
          No SOPs yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {visible.map((sop) => (
            <SopCard
              key={sop.id}
              sop={sop}
              coverUrl={sop.cover_image_path ? coverUrls[sop.cover_image_path] ?? null : null}
              canManage={canManage}
              onPreview={preview}
              onDownload={download}
              onEdit={canManage ? openEdit : undefined}
              onDelete={canManage ? setConfirmDel : undefined}
            />
          ))}
        </div>
      )}

      {canManage && (
        <UploadSopDialog
          open={uploadOpen || editing !== null}
          onOpenChange={(v) => {
            if (!v) {
              setUploadOpen(false);
              setEditing(null);
              setEditingAssignees([]);
            }
          }}
          mode={editing ? "edit" : "create"}
          editing={editing}
          initialAssignees={editingAssignees}
          currentUserId={user?.id ?? null}
          allProfiles={profiles}
        />
      )}

      {/* In-app preview modal */}
      <Dialog open={!!previewSop} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-sm">{previewSop?.title ?? ""}</DialogTitle>
          </DialogHeader>
          {previewLoading && (
            <div className="flex h-[70vh] items-center justify-center text-sm text-muted-foreground">
              Loading…
            </div>
          )}
          {!previewLoading && previewUrl && previewSop && (
            previewSop.file_type === "image" ? (
              <img
                src={previewUrl}
                alt={previewSop.file_name}
                className="mx-auto max-h-[70vh]"
              />
            ) : previewSop.file_type === "pdf" ? (
              <iframe
                src={previewUrl}
                className="h-[70vh] w-full rounded border"
                title={previewSop.file_name}
              />
            ) : (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Preview not available for this file type. Use Download instead.
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDel !== null} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this SOP?</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-muted-foreground">
            "{confirmDel?.title}" will be moved to trash and hidden from everyone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDel && delMut.mutate(confirmDel)}
              disabled={delMut.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
